import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";

/**
 * Schema for individual order submission result.
 */
const submissionResultSchema = z.object({
  stagedPurchaseOrderId: z.string().uuid(),
  salesOrderId: z.string().uuid().nullable(),
  success: z.boolean(),
  error: z.string().optional(),
});

/**
 * Submit Sales Orders Step
 *
 * Automatically approves staged purchase orders, creating sales orders
 * and syncing them to the configured ERP system.
 *
 * This step is designed for automated approval workflows where orders
 * that pass all validation checks can be submitted without human intervention.
 *
 * Important: This step uses `autoApproveSalesOrderDraft` which:
 * - Does not require a userId (creatorId will be null)
 * - Records the workflowRunId for audit trail
 * - Emits events with approvalMethod: 'automated'
 */
export const submitSalesOrdersStep = defineStepConfig({
  type: "submit_sales_orders",
  name: "Submit Sales Orders",
  description:
    "Automatically approves staged POs and creates sales orders in the ERP system",
  category: "Data Access",
  libraryModule: "OrderManagement",
  deprecated: {
    reason:
      "Superseded by the workflow-native draft-sales-order flow. Use 'Submit Draft Sales Orders', which creates the ERP SalesOrder + line items from relational draft sales orders.",
    replacedBy: "submit_draft_sales_orders",
  },
  inputSchema: z.object({
    stagedPurchaseOrderIds: z
      .array(z.string().uuid())
      .describe("UUIDs of the staged purchase orders to submit"),
  }),

  outputSchema: z.object({
    skipped: z
      .boolean()
      .default(false)
      .describe("Whether the step was skipped (e.g., auto-submit disabled)"),
    skippedReason: z
      .string()
      .optional()
      .describe("Reason for skipping, if skipped is true"),
    results: z
      .array(submissionResultSchema)
      .describe("Results for each order submission attempt"),
    submittedOrderIds: z
      .array(z.string().uuid())
      .describe("IDs of successfully submitted staged POs"),
    salesOrderIds: z
      .array(z.string().uuid())
      .describe("IDs of created sales orders"),
    failedOrderIds: z
      .array(z.string().uuid())
      .describe("IDs of staged POs that failed to submit"),
    successCount: z
      .number()
      .describe("Number of orders successfully submitted"),
    failureCount: z.number().describe("Number of orders that failed to submit"),
  }),

  configSchema: z.object({
    continueOnError: z
      .boolean()
      .default(true)
      .describe(
        "If true, continue processing remaining orders even if one fails. If false, stop on first failure.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});

export type SubmissionResult = z.infer<typeof submissionResultSchema>;
